import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import {
    Button,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    Spinner,
    useDisclosure,
} from "@heroui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faClose,
    faDrawPolygon,
    faPlus,
    faRotateLeft,
    faSave,
    faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { Feature, FeatureCollection, MultiPolygon, Polygon } from "geojson";
import useMap from "@/lib/map/useMap";
import { FIELD_BOUNDARY_DRAW_OPTIONS } from "@/lib/map/layers";
import useRequest from "@/lib/request/useRequest";
import { Imagery } from "@/types/imagery";

function multipolygonToFeatureCollection(
    geometry: MultiPolygon | Polygon,
): FeatureCollection<Polygon> {
    if (geometry.type === "Polygon") {
        return {
            type: "FeatureCollection",
            features: [
                {
                    type: "Feature",
                    geometry,
                    properties: {},
                },
            ],
        };
    }
    return {
        type: "FeatureCollection",
        features: geometry.coordinates.map((coordinates) => ({
            type: "Feature" as const,
            geometry: {
                type: "Polygon" as const,
                coordinates,
            },
            properties: {},
        })),
    };
}

function geometryPolygonCount(
    geometry?: MultiPolygon | Polygon | null,
): number {
    if (!geometry) return 0;
    if (geometry.type === "Polygon") return 1;
    return geometry.coordinates.length;
}

function drawFeaturesToMultiPolygon(
    features: Feature[],
): MultiPolygon | undefined {
    const coordinates = features
        .filter(
            (feature): feature is Feature<Polygon> =>
                feature.geometry?.type === "Polygon",
        )
        .map((feature) => feature.geometry.coordinates);
    if (!coordinates.length) return undefined;
    return {
        type: "MultiPolygon",
        coordinates,
    };
}

function DrawUsableBoundary({
    image,
    onClose,
    onSaved,
}: {
    image: Imagery;
    onClose: () => void;
    onSaved: () => void;
}) {
    const { map, mapLoaded } = useMap();
    const { useGetRequest, useMutateRequest } = useRequest();
    const draw = useRef<MapboxDraw | null>(null);
    const [livePolygonCount, setLivePolygonCount] = useState<number | null>(
        null,
    );
    const {
        isOpen: isClearOpen,
        onOpen: onClearOpen,
        onOpenChange: onClearOpenChange,
        onClose: onClearClose,
    } = useDisclosure();

    const userBoundary = useGetRequest<MultiPolygon | Polygon>({
        url: `imagery/${image.id}/usable_imagery_boundary/`,
        params: { source: "user" },
        queryOptions: {
            retry: false,
        },
    });

    const hasSavedUserBoundary = useMemo(
        () => !!userBoundary.data && userBoundary.isSuccess,
        [userBoundary.data, userBoundary.isSuccess],
    );
    const polygonCount =
        livePolygonCount ?? geometryPolygonCount(userBoundary.data);

    const saveBoundary = useMutateRequest<MultiPolygon>({
        method: "put",
        url: `imagery/${image.id}/usable_imagery_boundary/`,
        successMessage: "Usable boundary saved",
        errorMessage: "Failed to save usable boundary",
    });

    const clearBoundary = useMutateRequest({
        method: "put",
        url: `imagery/${image.id}/usable_imagery_boundary/`,
        successMessage: "User boundary cleared",
        errorMessage: "Failed to clear user boundary",
    });

    const syncPolygonCount = useCallback(() => {
        const count =
            draw.current
                ?.getAll()
                .features.filter(
                    (feature) => feature.geometry?.type === "Polygon",
                ).length ?? 0;
        setLivePolygonCount(count);
    }, []);

    const teardownDraw = useCallback(() => {
        if (!map || !draw.current) return;
        if (map.hasControl(draw.current)) {
            map.removeControl(draw.current);
        }
        draw.current = null;
        map.getCanvas().style.cursor = "";
    }, [map]);

    useEffect(() => {
        if (!map || !mapLoaded || draw.current || userBoundary.isLoading) return;

        const drawControl = new MapboxDraw(FIELD_BOUNDARY_DRAW_OPTIONS);
        draw.current = drawControl;
        map.addControl(drawControl, "bottom-right");

        map.on("draw.create", syncPolygonCount);
        map.on("draw.update", syncPolygonCount);
        map.on("draw.delete", syncPolygonCount);

        return () => {
            map.off("draw.create", syncPolygonCount);
            map.off("draw.update", syncPolygonCount);
            map.off("draw.delete", syncPolygonCount);
            teardownDraw();
        };
    }, [map, mapLoaded, syncPolygonCount, teardownDraw, userBoundary.isLoading]);

    const resetDraw = useCallback(() => {
        if (!map || !draw.current) return;

        draw.current.deleteAll();
        setLivePolygonCount(null);

        if (userBoundary.data && userBoundary.isSuccess) {
            const collection = multipolygonToFeatureCollection(
                userBoundary.data,
            );
            draw.current.add(collection);
            const firstId = draw.current.getAll().features[0]?.id;
            if (firstId != null) {
                // @ts-expect-error mapbox-gl-draw typing
                draw.current.changeMode("direct_select", {
                    featureId: firstId,
                });
            }
            map.getCanvas().style.cursor = "";
        } else {
            draw.current.changeMode("draw_polygon");
            map.getCanvas().style.cursor = "crosshair";
        }
    }, [map, userBoundary.data, userBoundary.isSuccess]);

    useEffect(() => {
        if (!map || !mapLoaded || !draw.current || userBoundary.isLoading) {
            return;
        }
        resetDraw();
    }, [
        map,
        mapLoaded,
        resetDraw,
        userBoundary.isLoading,
        userBoundary.dataUpdatedAt,
    ]);

    const handleAddPolygon = useCallback(() => {
        if (!draw.current || !map) return;
        draw.current.changeMode("draw_polygon");
        map.getCanvas().style.cursor = "crosshair";
    }, [map]);

    const handleSave = useCallback(() => {
        if (!draw.current) return;
        const multipolygon = drawFeaturesToMultiPolygon(
            draw.current.getAll().features,
        );
        if (!multipolygon) return;
        saveBoundary.mutate(
            { data: multipolygon },
            {
                onSuccess: () => {
                    onSaved();
                    onClose();
                },
            },
        );
    }, [onClose, onSaved, saveBoundary]);

    const handleClear = useCallback(() => {
        clearBoundary.mutate(
            { data: null },
            {
                onSuccess: () => {
                    resetDraw();
                    onClearClose();
                    userBoundary.refetch();
                    onSaved();
                },
            },
        );
    }, [clearBoundary, onClearClose, onSaved, resetDraw, userBoundary]);

    return (
        <>
            <div className="flex flex-col h-auto sm:h-[8rem] overflow-scroll w-full gap-2 py-2">
                <div className="flex flex-row justify-between items-center gap-2 px-2 shrink-0">
                    <div className="flex flex-col gap-0.5 min-w-0">
                        <span className="font-semibold text-sm">
                            Draw usable boundary
                        </span>
                        <span className="text-xs text-default-500">
                            Click to place vertices · Delete points with the
                            trash icon in the bottom right corner
                        </span>
                    </div>
                    <Button
                        isIconOnly
                        size="sm"
                        variant="light"
                        onPress={onClose}
                        aria-label="Close"
                    >
                        <FontAwesomeIcon icon={faClose} />
                    </Button>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-2 w-full">
                    <div className="flex flex-row items-center gap-2 text-xs text-default-500">
                        {userBoundary.isLoading ? (
                            <>
                                <Spinner size="sm" />
                                Loading boundary…
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faDrawPolygon} />
                                {polygonCount} polygon
                                {polygonCount === 1 ? "" : "s"}
                            </>
                        )}
                    </div>
                    <div className="flex flex-row flex-wrap items-center gap-2">
                        <Button
                            size="sm"
                            variant="flat"
                            aria-label="Add polygon"
                            onPress={handleAddPolygon}
                            isDisabled={userBoundary.isLoading}
                        >
                            <FontAwesomeIcon icon={faPlus} />
                            Add polygon
                        </Button>
                        <Button
                            size="sm"
                            variant="flat"
                            aria-label="Reset draw"
                            onPress={resetDraw}
                            isDisabled={userBoundary.isLoading}
                        >
                            <FontAwesomeIcon icon={faRotateLeft} />
                            Reset
                        </Button>
                        <Button
                            size="sm"
                            variant="flat"
                            color="danger"
                            aria-label="Clear user boundary"
                            onPress={onClearOpen}
                            isDisabled={
                                userBoundary.isLoading || !hasSavedUserBoundary
                            }
                            isLoading={clearBoundary.isPending}
                        >
                            <FontAwesomeIcon icon={faTrash} />
                            Clear
                        </Button>
                        <Button
                            size="sm"
                            color="primary"
                            aria-label="Save usable boundary"
                            onPress={handleSave}
                            isDisabled={
                                clearBoundary.isPending || polygonCount === 0
                            }
                            isLoading={saveBoundary.isPending}
                        >
                            <FontAwesomeIcon icon={faSave} />
                            Save
                        </Button>
                    </div>
                </div>
            </div>
            <Modal isOpen={isClearOpen} onOpenChange={onClearOpenChange}>
                <ModalContent>
                    {(close) => (
                        <>
                            <ModalHeader>Clear user boundary?</ModalHeader>
                            <ModalBody>
                                This removes the saved user boundary. The model
                                boundary will be used again if one exists.
                            </ModalBody>
                            <ModalFooter>
                                <Button
                                    size="sm"
                                    variant="light"
                                    onPress={close}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    size="sm"
                                    color="danger"
                                    onPress={handleClear}
                                    isLoading={clearBoundary.isPending}
                                >
                                    Clear
                                </Button>
                            </ModalFooter>
                        </>
                    )}
                </ModalContent>
            </Modal>
        </>
    );
}

export default DrawUsableBoundary;
